import React, {
  useEffect,
  useCallback,
  useState,
  useRef,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import type { AppsSidebarProps, AppItem } from "./types";
import { filterCategoriesByEnvironment } from "./environmentVisibility.config";
import { UserProfileSection, SidebarFooter } from "./components";
import { AppsMenu } from "./components/AppsMenu";
import {
  trackSidebarOpen,
  trackSidebarClose,
  trackSidebarScroll,
  trackAppClick,
  trackAppNavigation,
  trackLockedAppAttempt,
  startSidebarSession,
  endSidebarSession,
  trackRepeatAppSelection,
  trackVisibleCategoriesCount,
  trackSidebarOpenFrequency,
  resetPageSessionOpenCount,
} from "./sidebarAnalytics";
import { useUserStore } from "../../../store/userStore";
import { useAppTransitionStore } from "../../../store/appTransitionStore";
import AppsRail from "../AppsRail/AppsRail";
import { OPEN_APPS_EVENT } from "../AppsRail/favoriteApps";

export default function AppsSidebar({
  isOpen,
  onClose,
  categories,
  selectedAppId,
  onAppSelect,
  title,
  isDarkMode = true,
  currentTheme = "system",
  onThemeChange,
  userInfo,
  onProfileClick,
  onLogout,
  isLoggingOut = false,
  isEmbedded = false,
  isProfileActive = false,
  hideUserCard = false,
}: AppsSidebarProps) {
  const { t } = useTranslation("common");
  const user = useUserStore((state) => state.user);
  // El botón de apps del encabezado abre este menú por evento, sin que cada
  // layout (27 copias de handleAppSelect, 34 montajes) tenga que exponer un onOpen.
  const [openedFromRail, setOpenedFromRail] = useState(false);
  const open = isOpen || openedFromRail;
  // Dos modos (Daniel, 26-oct): el avatar del header (isOpen, lo que pasa cada
  // layout) abre el menú de PERFIL; el botón de apps junto al avatar abre las
  // APPS. Antes el avatar abría todo junto.
  const mode: "profile" | "apps" = openedFromRail ? "apps" : "profile";

  useEffect(() => {
    const openApps = () => setOpenedFromRail(true);
    window.addEventListener(OPEN_APPS_EVENT, openApps);
    return () => window.removeEventListener(OPEN_APPS_EVENT, openApps);
  }, []);
  const close = useCallback(() => {
    setOpenedFromRail(false);
    onClose();
  }, [onClose]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const lastScrollDepthRef = useRef<number>(0);
  const appsViewedRef = useRef<Set<string>>(new Set());

  const filteredCategories = useMemo(
    () => filterCategoriesByEnvironment(categories),
    [categories],
  );

  const checkScrollState = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        scrollContainerRef.current;

      const scrollDepth =
        scrollHeight > clientHeight
          ? Math.round((scrollTop / (scrollHeight - clientHeight)) * 100)
          : 0;
      const depthThresholds = [25, 50, 75, 100];
      for (const threshold of depthThresholds) {
        if (
          scrollDepth >= threshold &&
          lastScrollDepthRef.current < threshold
        ) {
          trackSidebarScroll(threshold);
        }
      }
      lastScrollDepthRef.current = scrollDepth;
    }
  }, []);

  useEffect(() => {
    if (open) {
      trackSidebarOpen(window.location.pathname);
      trackSidebarOpenFrequency();
      startSidebarSession();
      lastScrollDepthRef.current = 0;
      appsViewedRef.current = new Set();
      trackVisibleCategoriesCount(filteredCategories.length, categories.length);
    } else {
      endSidebarSession(
        appsViewedRef.current.size,
        filteredCategories.length,
        filteredCategories.length,
        user?.role || undefined,
      );
    }
  }, [open]);

  useEffect(() => {
    resetPageSessionOpenCount();
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        trackSidebarClose("escape_key");
        close();
      }
    },
    [close],
  );

  useEffect(() => {
    if (open) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [open, handleKeyDown]);

  // Toda navegación a OTRA app (menú o barra) pasa bajo la cortina "Abriendo
  // <app>…" (ver appTransitionStore): la app destino monta tapada. Las que no
  // navegan (próximamente, bloqueadas, la app actual) van directo al
  // onAppSelect del layout, que muestra su aviso de siempre.
  const selectApp = useCallback(
    (app: AppItem) => {
      const navigates =
        !!app.route && !app.isComingSoon && !app.isLocked && !app.disabled && app.id !== selectedAppId;
      if (!navigates) {
        onAppSelect?.(app);
        return;
      }
      useAppTransitionStore.getState().start(app, isDarkMode, () => {
        setOpenedFromRail(false);
        onAppSelect?.(app);
      });
    },
    [onAppSelect, selectedAppId, isDarkMode],
  );

  const handleAppClick = useCallback(
    (app: AppItem, categoryId: string, categoryTitle: string) => {
      const appStatus = app.isComingSoon
        ? ("coming_soon" as const)
        : app.isLocked
          ? ("locked" as const)
          : app.disabled
            ? ("disabled" as const)
            : app.isBeta
              ? ("beta" as const)
              : ("active" as const);

      if (app.disabled || app.isComingSoon || app.isLocked) {
        trackLockedAppAttempt(
          app.id,
          app.name,
          categoryId,
          app.isComingSoon
            ? "coming_soon"
            : app.isLocked
              ? "locked"
              : "disabled",
        );
        if (app.disabled || app.isComingSoon) return;
      }

      trackAppClick(
        app.id,
        app.name,
        categoryId,
        categoryTitle,
        app.route || app.externalUrl || "",
        appStatus,
      );

      if (selectedAppId === app.id) {
        trackRepeatAppSelection(app.id, app.name, categoryId);
      }

      if (selectedAppId && selectedAppId !== app.id) {
        trackAppNavigation(
          selectedAppId,
          app.id,
          categoryId,
          app.route || app.externalUrl || "",
        );
      }

      appsViewedRef.current.add(app.id);
      selectApp(app);
    },
    [selectApp, selectedAppId],
  );

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) {
        trackSidebarClose("backdrop_click");
        close();
      }
    },
    [close],
  );

  const rail = (
    <AppsRail
      categories={filteredCategories}
      currentAppId={selectedAppId}
      onAppSelect={selectApp}
      isDarkMode={isDarkMode}
    />
  );

  if (!open) return rail;

  const heading = title || t("appsSidebar.title");
  // Tarjeta flotante bajo el encabezado, con un vidrio leve (Daniel, 30-oct,
  // referencia ElevenLabs): la comparten el menú de perfil y el de apps.
  const popover = `absolute right-3 top-[3.25rem] max-h-[calc(100dvh-4.5rem)] rounded-2xl border shadow-[0_12px_40px_rgba(0,0,0,0.14)] backdrop-blur-xl ${
    isDarkMode ? "bg-[#1f1f1f]/95 border-white/10" : "bg-white/95 border-black/[0.08]"
  }`;
  const popIn = { animation: "popIn 160ms cubic-bezier(0.2, 0, 0, 1)", transformOrigin: "top right" };
  const popInKeyframes = (
    <style>{`
      @keyframes popIn {
        from { opacity: 0; transform: scale(0.97) translateY(-2px); }
        to { opacity: 1; transform: scale(1) translateY(0); }
      }
      @media (prefers-reduced-motion: reduce) {
        [role="dialog"] { animation: none !important; }
      }
    `}</style>
  );

  if (!isEmbedded && mode === "profile") {
    return (
      <>
        {rail}
        <div className="fixed inset-0 z-50" onClick={handleBackdropClick}>
          <div
            role="dialog"
            aria-label={t("appsSidebar.profileMenu", "Menú de perfil")}
            className={`${popover} w-[min(18rem,calc(100vw-1rem))] overflow-y-auto p-1.5`}
            style={popIn}
          >
            {userInfo && (
              <UserProfileSection
                userInfo={userInfo}
                onProfileClick={() => {
                  close();
                  onProfileClick?.();
                }}
                currentTheme={currentTheme}
                onThemeChange={onThemeChange}
                onLogout={onLogout}
                isLoggingOut={isLoggingOut}
                isDarkMode={isDarkMode}
                isProfileActive={isProfileActive}
                hideUserCard={false}
              />
            )}
          </div>
          {popInKeyframes}
        </div>
      </>
    );
  }

  const appsPanel = (
    <div
      role="dialog"
      aria-label={heading}
      className={`flex flex-col ${
        isEmbedded
          ? `h-[100dvh] w-full sm:w-96 max-w-full ${isDarkMode ? "bg-dark-bg" : "bg-white"}`
          : `${popover} w-[min(40rem,calc(100vw-1.5rem))]`
      }`}
      style={isEmbedded ? undefined : popIn}
    >
      <h2 className={`px-4 pt-3.5 pb-2.5 text-[17px] font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
        {heading}
      </h2>
      <div
        ref={scrollContainerRef}
        onScroll={checkScrollState}
        className="flex-1 min-h-0 overflow-y-auto px-1.5 pb-1.5"
      >
        <AppsMenu
          categories={filteredCategories}
          selectedAppId={selectedAppId}
          onAppClick={handleAppClick}
          isDarkMode={isDarkMode}
          twoColumns={!isEmbedded}
        />

        {/* El perfil vive en el menú del avatar; aquí sólo cuando la
            appsuite va embebida (no hay header que lo abra). */}
        {isEmbedded && userInfo && (
          <div className="mt-3">
            <UserProfileSection
              userInfo={userInfo}
              onProfileClick={onProfileClick}
              currentTheme={currentTheme}
              onThemeChange={onThemeChange}
              onLogout={onLogout}
              isLoggingOut={isLoggingOut}
              isDarkMode={isDarkMode}
              isProfileActive={isProfileActive}
              hideUserCard={hideUserCard}
            />
          </div>
        )}
      </div>

      <SidebarFooter isDarkMode={isDarkMode} />
    </div>
  );

  if (isEmbedded) {
    return (
      <>
        {rail}
        {appsPanel}
      </>
    );
  }

  return (
    <>
      {rail}
      <div className="fixed inset-0 z-50" onClick={handleBackdropClick}>
        {appsPanel}
        {popInKeyframes}
      </div>
    </>
  );
}
